// Board-click actions: the city build/train suggestions and the "move or sail
// the selected unit" click. Extracted from App.tsx (ROADMAP 7.14); the hook
// order is unchanged.

import type { CitySuggestion } from '@aod/sim-adapter';
import { NAVAL_KINDS } from '@aod/ui/prefabs';
import { useCallback } from 'react';
import { tryChampionCast } from '../champion/castAbility.js';
import type { SimWorld } from '../sim/useSimWorld/index.js';
import { suggestionAction, useCitySuggestions } from '../sim/useSimWorld/suggestions.js';
import type { Selection } from '../testBridge/useTestBridge.js';

export function useBoardActions(sim: SimWorld, selection: Selection, suggestMode: boolean) {
  // Spec §8 suggest mode: while a city is selected, poll the sim's top
  // build/train ideas for the local seat. Applying one routes through the
  // normal start_building intent (the wasm infers units from the id) and
  // re-pulls so the remaining buttons reflect the new queue.
  const { ideas: suggestions, refresh: refreshSuggestions } = useCitySuggestions({
    enabled: suggestMode && selection?.kind === 'city',
    getIdeas: () => sim.suggestIntents(),
  });
  const handleApplySuggestion = useCallback(
    (idea: CitySuggestion) => {
      const action = suggestionAction(idea);
      if (!action) return;
      if (sim.startBuilding(action.cityId, action.buildingId)) {
        refreshSuggestions();
      }
    },
    [sim, refreshSuggestions],
  );

  const handleMoveClick = useCallback(
    (q: number, r: number) => {
      if (tryChampionCast({ snapshot: sim.snapshot, activate: sim.activateLegendAbility }, q, r)) {
        return;
      }
      if (selection?.kind === 'unit') {
        // Ships plot a course over water; everything else steps one hex.
        const node = sim.snapshot?.scene?.children?.find(
          (n) => n.id === selection.id && n.type === 'unit',
        );
        const kind = node?.props?.kind as string | undefined;
        if (kind && NAVAL_KINDS.has(kind)) sim.sailShip(selection.id, q, r);
        else sim.moveUnit(selection.id, q, r);
      }
    },
    [selection, sim],
  );

  return { suggestions, handleApplySuggestion, handleMoveClick };
}
